// @flow

import { ThemeProvider } from '@emotion/react'
import styled from '@emotion/styled'
//import * as Sentry from '@sentry/gatsby'
import { Link, type PageProps, graphql } from 'gatsby'
import { GatsbyImage, StaticImage, getImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { colors, fonts, media, themes } from '@toggl/style'
import {
  Brands,
  Button,
  CarouselNormal,
  ContainerReadMore,
  ContainerSectionTitle,
  Hero,
  IconPlay,
  SectionBlock,
  SectionModalVideo,
  text,
} from '@toggl/ui'
import { LayoutTrack } from 'components/layouts'

import { getTrackSignupUrl } from '../utils'

import typeof IndexPageYaml from './index.yml'

declare var globalThis: any
declare var process: WebProcess

function ReviewG2({
  human,
  text: displayText,
  bordered,
}: {
  human: string,
  text: string,
  bordered?: boolean,
}) {
  return (
    <ReviewG2Box data-bordered={bordered}>
      <img
        src={require('./assets/icon-g2-stars.svg').default}
        height='20'
        alt='G2 logo and five stars'
      />

      <text.P3 color={colors.darkPurple90}>“{displayText}”</text.P3>
      <text.P4 color={colors.darkPurple60}>{human}</text.P4>
    </ReviewG2Box>
  )
}

const words = ['micromanaging', 'spying on', 'giving more work to']
const wordBubbleColors = [
  colors.darkPurple90,
  colors.darkPurple90,
  colors.darkPurple90,
]

const AnimatedText = () => {
  const [currentWord, setCurrentWord] = React.useState(words[0])
  const [animate, setAnimate] = React.useState(false)
  const wordRef = React.useRef(null)
  const bubbleRef = React.useRef(null)
  const [bubbleColor, setBubbleColor] = React.useState(wordBubbleColors[0]) // Start with the first color

  React.useEffect(() => {
    const changeWord = () => {
      setAnimate(true)
      setTimeout(() => {
        setAnimate(false)
        const nextIndex = (words.indexOf(currentWord) + 1) % words.length
        setCurrentWord(words[nextIndex])
        setBubbleColor(wordBubbleColors[nextIndex])
      }, 500) // This timeout duration should match the fade-out animation
    }

    // Set the width initially
    if (bubbleRef.current && wordRef.current) {
      bubbleRef.current.style.width = `${wordRef.current.offsetWidth + 56}px`
    }

    const intervalId = setInterval(changeWord, 2400) // Change word every 2.4 seconds

    return () => clearInterval(intervalId)
  }, [currentWord])

  React.useEffect(() => {
    // Update the bubble width when `animate` changes back to false, i.e., when a new word has faded in
    if (bubbleRef.current && wordRef.current && !animate) {
      bubbleRef.current.style.width = `${wordRef.current.offsetWidth + 56}px`
    }
  }, [animate])

  return (
    <Bubble>
      Time tracking? Do you mean <br />
      <WordBubble
        ref={bubbleRef}
        style={{
          transition:
            'width 0.5s ease-in-out, background-color 0.5s ease-in-out',
          backgroundColor: bubbleColor,
        }}
      >
        <TextCycle ref={wordRef} className={`${animate ? 'fade-out' : ''}`}>
          {currentWord}
        </TextCycle>
      </WordBubble>{' '}
      <AfterBubbleText>my employees?</AfterBubbleText>
    </Bubble>
  )
}

const IndexPage = ({
  data,
}: PageProps<{
  indexYaml: IndexPageYaml,
}>) => {
  const {
    indexYaml: { caseStudies, contactSalesPoints, personas, testimonials },
  } = data

  return (
    <LayoutTrack
      title={'Toggl Track: Time Tracking Software for Any Workflow'}
      description={
        "The world's best time tracking software. Track employee time, send beautiful client reports, and calculate profitability. Free and paid plans to save you time."
      }
      showRatingSchema
    >
      <ThemeProvider theme={themes.track}>
        <HeroWrapperStyled>
          <HeroContentWrapper>
            <HeroContent>
              <HeroLeftContent>
                <HeroTitle>
                  <text.Rotalic color={colors.pink100}>
                    Frictionless
                  </text.Rotalic>{' '}
                  time tracking software for teams
                </HeroTitle>
                <HeroSubtitle color={colors.orange10}>
                  Give your team a time tracker they’ll love, and help your
                  managers make profitable project decisions with meaningful
                  time data insights.
                </HeroSubtitle>

                <ButtonWrapper>
                  <Button.Primary
                    data-heap-id='track-signup'
                    as='a'
                    href={getTrackSignupUrl()}
                  >
                    Start tracking for free
                  </Button.Primary>
                  <SectionModalVideo
                    cta={
                      <VideoButton>
                        <IconPlay color={colors.peach10} />
                        See how it works
                      </VideoButton>
                    }
                  >
                    <iframe
                      src={'https://www.youtube.com/embed/y_xmxEE13Hg'}
                      width='100%'
                      height='100%'
                      frameBorder='0'
                      allow='autoplay; encrypted-media'
                      allowFullScreen
                    />
                  </SectionModalVideo>
                </ButtonWrapper>
              </HeroLeftContent>
              <HeroRightContent>
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/toggl-track-reports.png'}
                  alt={
                    'Screenshot of the Toggl Track reports accompanied by time tracking elements such as mobile and desktop app, and over 100+ integrations with Toggl Track for easier time tracking'
                  }
                  height={1100}
                  placeholder='none'
                  objectFit='contain'
                />
              </HeroRightContent>
            </HeroContent>
          </HeroContentWrapper>
        </HeroWrapperStyled>

        <SocialProof>
          <SizeWrapper>
            <Brands
              image={
                <StaticImage
                  src='./assets/brands-mixed-desktop-light.png'
                  alt='Companies that use Toggl Track: Amazon, Uber, GE, LinkedIn, SAP, Danfoss, Wise, Ogilvy, Booking.com'
                  width={1120}
                  formats={['auto', 'webp', 'avif']}
                  placeholder='none'
                  objectFit='contain'
                />
              }
              mobileImage={
                <StaticImage
                  src='./assets/brands-mixed-mobile-light.png'
                  alt='Companies that use Toggl Track: Amazon, Uber, GE, LinkedIn, SAP, Danfoss, Wise, Ogilvy'
                  width={450}
                  formats={['auto', 'webp', 'avif']}
                  placeholder='none'
                  objectFit='contain'
                />
              }
            />
          </SizeWrapper>
        </SocialProof>

        <TrackUseCases />

        <SectionBlock background={colors.darkPurple100}>
          <AnimatedText />
          <ResponseBlock>
            <text.H2 color={colors.peach10}>
              No, we do not. At Toggl, we see time as an{' '}
              <text.Rotalic color={colors.pink80}>opportunity.</text.Rotalic>
            </text.H2>
            <BigText color={colors.peach10}>
              An opportunity to convert time tracked into actionable insights
              that boost productivity, increase efficiency and balance team
              workloads. So you can drive <span>profitability and growth</span>{' '}
              while prioritising your team's <span>trust and well-being</span>.
            </BigText>
          </ResponseBlock>

          <text.UppercaseOneLiner color={colors.peach10}>
            Companies that use Toggl Track see:
          </text.UppercaseOneLiner>

          <ContainerReadMore>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyBox key={index}>
                <text.H3Small color={colors.darkPurple90}>
                  <span>{caseStudy.bigStat}</span>
                  {caseStudy.mediumText}
                </text.H3Small>
                <text.P2 color={colors.darkPurple70}>
                  {caseStudy.smallText}
                </text.P2>

                <CaseStudyImage>
                  <GatsbyImage
                    image={getImage(caseStudy.image.src)}
                    alt={caseStudy.image.alt}
                    objectFit='contain'
                  />
                </CaseStudyImage>

                <ArrowedButton as={Link} to={caseStudy.link}>
                  Read case study{' '}
                  <img
                    src={require('./assets/icon-right-rounded.svg').default}
                    alt='Right arrow icon'
                    loading='lazy'
                  />
                </ArrowedButton>
              </CaseStudyBox>
            ))}
          </ContainerReadMore>

          <WhyTrackCTA
            title={
              'Want better insights on how your team is spending their time?'
            }
            titleColor={colors.peach10}
            subTitle={
              <SignUpPoints>
                <SUPoint color={colors.darkPurple20}>
                  <img
                    src={require('./assets/icon-check-ghost.svg').default}
                    width='26'
                    alt='Checkmark icon'
                  />
                  No credit card required
                </SUPoint>
                <SUPoint color={colors.darkPurple20}>
                  <img
                    src={require('./assets/icon-check-ghost.svg').default}
                    width='26'
                    alt='Checkmark icon'
                  />
                  Free 30-day trial for all features
                </SUPoint>
                <SUPoint color={colors.darkPurple20}>
                  <img
                    src={require('./assets/icon-check-ghost.svg').default}
                    width='26'
                    alt='Checkmark icon'
                  />
                  Cancel anytime
                </SUPoint>
              </SignUpPoints>
            }
            cta={
              <>
                <Button.Primary as='a' href={getTrackSignupUrl()}>
                  Start tracking for free
                </Button.Primary>
                <Button.Secondary
                  as={Link}
                  to='/track/customers/'
                  caretDirection='right'
                  color={colors.peach10}
                >
                  Or read more case studies
                </Button.Secondary>
              </>
            }
          />
        </SectionBlock>

        <SectionBlock background={colors.peach20}>
          <H2CenterTitle color={colors.darkPurple90}>
            <text.Rotalic>The</text.Rotalic> non-intrusive time tracker for
            self-driven teams
          </H2CenterTitle>

          <CarouselNormal show={3}>
            {personas.map((persona, index) => (
              <PeerReviewWrapper key={index}>
                <PersonaBox as={Link} to={persona.link}>
                  <text.H4 color={colors.darkPurple90}>{persona.title}</text.H4>
                  <text.P2 color={colors.darkPurple70}>
                    {persona.paragraph}
                  </text.P2>

                  <PersonaFinal>
                    <text.H5 color={colors.darkPurple100}>
                      {persona.finalBenefit}
                    </text.H5>
                  </PersonaFinal>

                  <ArrowedButton as='div'>
                    Learn more
                    <img
                      src={require('./assets/icon-right-rounded.svg').default}
                      alt='Right arrow icon'
                      loading='lazy'
                    />
                  </ArrowedButton>
                  <GatsbyImage
                    image={getImage(persona.image.src)}
                    alt={persona.image.alt}
                    objectFit='cover'
                    objectPosition='top center'
                  />
                </PersonaBox>
              </PeerReviewWrapper>
            ))}
          </CarouselNormal>

          <ContactSales>
            <ContactSalesCTA>
              <text.H3Small color={colors.darkPurple90}>
                Onboarding a large team? We’d love to show you around.
              </text.H3Small>
              <text.P2 color={colors.darkPurple70}>
                Teams of 10+ are eligible for a custom demo to see how Toggl
                Track works at scale.
              </text.P2>
            </ContactSalesCTA>

            <Button.Primary as={Link} to='/track/demo-request/'>
              Book a demo
            </Button.Primary>
            <hr />
            {contactSalesPoints.map((contactSalesPoint, index) => (
              <SalesPoint key={index}>
                <img
                  src={require('./assets/icon-checkmark.png').default}
                  alt='Checkmark icon'
                  loading='lazy'
                />
                <text.P3 color={colors.darkPurple90}>
                  {contactSalesPoint.point}
                </text.P3>
              </SalesPoint>
            ))}
          </ContactSales>
        </SectionBlock>
      </ThemeProvider>

      <SectionBlock background={colors.peach50}>
        <H2CenterTitle color={colors.darkPurple90}>
          Why <CircleSketch>120,000+</CircleSketch> users started tracking time
          with Toggl Track in the last 30 days
        </H2CenterTitle>
        <ReasonsGrid>
          <ReasonsGridImage>
            <StaticImage
              src='./assets/award-capterra.png'
              alt='Capterra award: Voted best ease of use by 2256 reviews'
              width={220}
              formats={['auto', 'webp', 'avif']}
              placeholder='none'
              objectFit='contain'
            />
          </ReasonsGridImage>

          <ReasonsGridCard>
            <text.H4 color={colors.darkPurple90}>
              Easy for <text.Rotalic>both</text.Rotalic> team members and
              managers
            </text.H4>
            <text.P3 color={colors.darkPurple70}>
              So nobody views time tracking or reporting as a dreaded chore.
            </text.P3>
            <Separator />
            <ReviewG2
              text='Versatile and easy even if you’ve never used time tracking software before'
              human='Kathleen, Service Area Manager'
            />
          </ReasonsGridCard>

          <ReasonsGridCard>
            <text.H4 color={colors.darkPurple90}>Better than the best</text.H4>
            <text.P3 color={colors.darkPurple70}>
              <text.Strong>And</text.Strong> we're 100% against employee
              surveillance — use a time tracker that gets a "yes" from every
              employee.
            </text.P3>
            <Separator />
            <ReviewG2
              text='Chose Toggl Track after trialing 20+ similar time tracking tools'
              human='Andy, Software Consultant'
            />
          </ReasonsGridCard>

          <ReasonsGridCard>
            <text.H4 color={colors.darkPurple90}>Top-rated support</text.H4>
            <text.P3 color={colors.darkPurple70}>
              With a dedicated customer success manager for enterprise teams.
            </text.P3>
            <Separator />
            <ReviewG2
              text='Regular updates ensure that any bugs are caught quickly and customer support responds within a day; these features make using Toggl Track a breeze.'
              human='Trevor, Senior Project Leader'
            />
          </ReasonsGridCard>

          <ReasonsGridCard>
            <text.H4 color={colors.darkPurple90}>
              Get your team going, faster
            </text.H4>
            <text.P3 color={colors.darkPurple70}>
              Low learning curve with no training necessary.
            </text.P3>
            <Separator />
            <ReviewG2
              text='No complicated setup or unnecessary bells & whistles!'
              human='Renee, Senior HR Coordinator'
            />
          </ReasonsGridCard>

          <ReasonsGridCard>
            <text.H4 color={colors.darkPurple90}>Track time anywhere</text.H4>
            <text.P3 color={colors.darkPurple70}>
              From web to integrations to mobile, there’s a time tracking app
              for everyone!
            </text.P3>
            <Separator />
            <ReviewG2
              text='Easy to use time tracking app for desktop and mobile'
              human='Rachel, Small business'
            />
          </ReasonsGridCard>
        </ReasonsGrid>
      </SectionBlock>

      <PeerReviewSection background={colors.peach50}>
        <IlloHeartHand>
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/illo-heart-hands.png'}
            alt={'Illustration of two hands making a heart'}
            height={110}
            placeholder='none'
            objectFit='contain'
          />
        </IlloHeartHand>

        <PeerReviewSectionTitle
          title='“The best free time tracking app”'
          titleSize='2'
          titleColor={colors.darkPurple90}
          subText='Small team and no budget? We have you covered'
        />
        <CarouselNormal show={1}>
          {testimonials.map((testimonial, index) => (
            <PeerReviewWrapper key={index}>
              <PeerReview>
                <img
                  src={require('./assets/icon-quotation.svg').default}
                  width='50'
                  alt='Quotation mark'
                />
                <text.P1 color={colors.peach10}>
                  {testimonial.paragraph}
                </text.P1>
                <GatsbyImage
                  image={getImage(testimonial.image.src)}
                  alt={testimonial.image.alt}
                  objectFit='contain'
                />
              </PeerReview>
            </PeerReviewWrapper>
          ))}
        </CarouselNormal>
      </PeerReviewSection>

      <LastSignUp>
        <WhyTrackCTA
          title={
            <text.H2 color={colors.darkPurple100}>
              Discover how time tracking can help you build a better business
              with higher profits and happy teams
            </text.H2>
          }
          subTitle={
            <text.P2 color={colors.darkPurple70}>
              Toggl Track's Free plan is free forever. Our Premium plan comes
              with a free, 30-day trial. No credit card required to get started.
            </text.P2>
          }
          cta={
            <>
              <Button.Primary as='a' href={getTrackSignupUrl()}>
                Start tracking for free
              </Button.Primary>
              <Button.Secondary
                as={Link}
                to='/track/demo-request/'
                caretDirection='right'
                color={colors.darkPurple80}
              >
                Or talk to Sales
              </Button.Secondary>
            </>
          }
        />
      </LastSignUp>
    </LayoutTrack>
  )
}

const TrackUseCases = () => {
  const [selectedTab, selectTab] = React.useState<
    | 'time-reporting'
    | 'time-billing'
    | 'project-time-tracking'
    | 'employee-time-tracking'
  >('time-reporting')
  const activateTimeReporting = React.useCallback(
    () => selectTab('time-reporting'),
    []
  )
  const activateTimeBilling = React.useCallback(
    () => selectTab('time-billing'),
    []
  )
  const activateProject = React.useCallback(
    () => selectTab('project-time-tracking'),
    []
  )

  const activateEmployee = React.useCallback(
    () => selectTab('employee-time-tracking'),
    []
  )

  return (
    <SectionBlock background={colors.peach10}>
      <H2CenterTitle color={colors.darkPurple90}>
        <text.Rotalic>Flexible</text.Rotalic> time tracking tool for your
        business needs
      </H2CenterTitle>

      <Tabs>
        <Tab
          active={selectedTab === 'time-reporting'}
          onClick={activateTimeReporting}
        >
          Time reporting
        </Tab>
        <Tab
          active={selectedTab === 'time-billing'}
          onClick={activateTimeBilling}
        >
          Time billing
        </Tab>
        <Tab
          active={selectedTab === 'project-time-tracking'}
          onClick={activateProject}
        >
          Project tracking
        </Tab>
        <Tab
          active={selectedTab === 'employee-time-tracking'}
          onClick={activateEmployee}
        >
          Team workload
        </Tab>
      </Tabs>
      <TabArea>
        {selectedTab === 'time-reporting' && (
          <TabItem>
            <TabItemText>
              <text.UppercaseOneLiner as='h3' color={colors.darkPurple70}>
                Insightful time reporting software
              </text.UppercaseOneLiner>
              <text.P1 color={colors.darkPurple90}>
                Know exactly how much time your team spends on tasks
              </text.P1>
              <text.P3 color={colors.darkPurple70}>
                Say goodbye to inaccurate weekly timesheets and get a handle on
                your team's exact billable and non-billable hours.
              </text.P3>
              <Button.Secondary
                as={Link}
                to='/track/online-time-reporting/'
                caretDirection='right'
                color={colors.pink80}
              >
                More about time reporting
              </Button.Secondary>
            </TabItemText>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/screen-time-reporting.png'}
                alt={
                  "Screenshot of Toggl Track's Analytics feature with widgets such as time tracked, total billables with trends, bar charts and pie charts of various organization metrics"
                }
                height={450}
                placeholder='none'
                objectFit='contain'
              />
            </TabImage>
            <ReviewG2
              bordered
              text='Time management software that helps improve metrics and estimates'
              human='Marie, Project Coordinator'
            />
          </TabItem>
        )}

        {selectedTab === 'time-billing' && (
          <TabItem>
            <TabItemText>
              <text.UppercaseOneLiner as='h3' color={colors.darkPurple70}>
                Accurate time billing software
              </text.UppercaseOneLiner>
              <text.P1 color={colors.darkPurple90}>
                Do you sell your time? We'll help you get the numbers right.
                Every time.
              </text.P1>
              <text.P3 color={colors.darkPurple70}>
                Set hourly rates, track billable time, create invoices, and
                share them with your clients to get paid on time.
              </text.P3>
              <Button.Secondary
                as={Link}
                to='/track/time-tracking-invoicing/'
                caretDirection='right'
                color={colors.pink80}
              >
                More about time billing
              </Button.Secondary>
            </TabItemText>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/screen-time-billing.png'}
                alt={
                  "Screenshot of Toggl Track's reports featuring billable hours and time tracked for clients and projects'"
                }
                height={450}
                placeholder='none'
                objectFit='contain'
              />
            </TabImage>
            <ReviewG2
              bordered
              text='The best tool for tracking, budgeting, and invoicing'
              human='Felicity, Enterprise'
            />
          </TabItem>
        )}

        {selectedTab === 'project-time-tracking' && (
          <TabItem>
            <TabItemText>
              <text.UppercaseOneLiner as='h3' color={colors.darkPurple70}>
                Project time tracking software
              </text.UppercaseOneLiner>
              <text.P1 color={colors.darkPurple90}>
                No more anxiety over late deadlines or unprofitable projects
              </text.P1>
              <text.P3 color={colors.darkPurple70}>
                Organize project tasks, track project hours, and compare
                estimated vs. actual time and costs to deliver projects
                profitably.
              </text.P3>

              <Button.Secondary
                as={Link}
                to='/track/project-time-tracking'
                caretDirection='right'
                color={colors.pink80}
              >
                More about project time tracking
              </Button.Secondary>
            </TabItemText>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/screen-project-time-tracking.png'}
                alt={
                  "Screenshot of Toggl Track's insights feature, showcasing project profitability with a line chart. The line chart has project earnings and labor cost data."
                }
                height={450}
                placeholder='none'
                objectFit='contain'
              />
            </TabImage>
            <ReviewG2
              bordered
              text='Changed our project time tracking into an organized, cohesive activity'
              human='Steve, Small Business'
            />
          </TabItem>
        )}

        {selectedTab === 'employee-time-tracking' && (
          <TabItem>
            <TabItemText>
              <text.UppercaseOneLiner as='h3' color={colors.darkPurple70}>
                Employee time tracking software
              </text.UppercaseOneLiner>
              <text.P1 color={colors.darkPurple90}>
                Improve team productivity and happiness without the
                micromanagement
              </text.P1>
              <text.P3 color={colors.darkPurple70}>
                Track employee work hours, generate timesheet reports, and
                balance resource workloads to keep the team from overworking or
                burning out.
              </text.P3>

              <Button.Secondary
                as={Link}
                to='/track/employee-time-tracking/'
                caretDirection='right'
                color={colors.pink80}
              >
                More about employee time tracking
              </Button.Secondary>
            </TabItemText>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/screen-employee-time-tracking.png'}
                alt={
                  "Screenshot of Toggl Track's timesheet weekly report with an additional widget showcasing team tracked by time"
                }
                height={450}
                placeholder='none'
                objectFit='contain'
              />
            </TabImage>
            <ReviewG2
              bordered
              text='Helps ensure there are no surprises in terms of deadlines, resources or efficiency'
              human='Breanna, Software Coder'
            />
          </TabItem>
        )}
      </TabArea>
    </SectionBlock>
  )
}

export const query = graphql`
  {
    indexYaml {
      caseStudies {
        bigStat
        mediumText
        smallText
        link
        image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                height: 28
                formats: [AUTO, WEBP, AVIF]
                placeholder: NONE
              )
            }
          }
        }
      }
      contactSalesPoints {
        point
      }
      testimonials {
        image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                width: 80
                height: 80
                formats: [AUTO, WEBP, AVIF]
                placeholder: NONE
              )
            }
          }
        }
        paragraph
      }
      personas {
        title
        link
        paragraph
        finalBenefit
        image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                width: 305
                height: 170
                formats: [AUTO, WEBP, AVIF]
                placeholder: NONE
              )
            }
          }
        }
      }
    }
  }
`

const ReviewG2Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  &[data-bordered] {
    border-left: 2px solid ${colors.darkPurple60};
    padding-left: 20px;
  }
  p:nth-of-type(1) {
    margin-top: 10px;
  }
`

const H2CenterTitle = styled(text.H2)`
  margin: 30px auto 45px;
  max-width: 700px;
  text-align: center;
  z-index: 1;
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: 40px 0 10px;
  column-gap: 20px;
  row-gap: 20px;
  a {
    display: inline-flex;
  }
  ${media.mq[2]} {
    justify-content: flex-start;
  }
`

const SocialProof = styled.div`
  padding: 30px;
  text-align: center;
  background: ${colors.darkPurple90};
  .gatsby-image-wrapper {
    width: 80%;
    margin: auto;
  }
`

const SizeWrapper = styled.div`
  max-width: 1140px;
  margin: auto;
`

const IlloHeartHand = styled.div`
  text-align: center;
  margin-bottom: 30px;
  ${media.mq[2]} {
    margin-top: -40px;
  }
`

const PeerReviewSectionTitle = styled(ContainerSectionTitle)`
  gap: 10px;
  margin-bottom: 30px;
  ${media.mq[1]} {
    margin-bottom: 5px;
  }
  z-index: -1;
  p {
    color: ${colors.darkPurple70};
  }
`

const HeroWrapperStyled = styled(Hero.Wrapper)`
  ${media.mq[2]} {
    overflow: clip;
  }
`

const HeroContentWrapper = styled(Hero.ContentWrapper)`
  ${media.mq[2]} {
    margin-top: 180px;
  }
`

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  ${media.mq[2]} {
    flex-direction: row;
    align-items: center;
    gap: 50px;
  }
`

const HeroLeftContent = styled.div`
  flex: 1;
`

const HeroRightContent = styled.div`
  text-align: center;
  ${media.mq[2]} {
    text-align: left;
    flex: 0 0 auto;
    max-width: 43%;
  }
  .gatsby-image-wrapper {
    width: 100%;
    max-width: 640px;
    ${media.mq[2]} {
      width: 130%;
      transform: translate(-15%, 2%);
    }
  }
`

const HeroTitle = styled(Hero.Title)`
  text-align: center;
  max-width: 90%;
  margin: auto;
  ${media.mq[2]} {
    text-align: left;
    margin: 20px 0 35px;
  }
`

const HeroSubtitle = styled(text.P1)`
  text-align: center;
  max-width: 90%;
  margin: 35px auto;
  ${media.mq[2]} {
    text-align: left;
    line-height: 1.5;
    margin: 35px 0 40px;
  }
`

const ContactSales = styled.div`
  background: ${colors.peach100};
  border-radius: 10px;
  padding: 20px 30px;
  display: grid;
  grid-template-columns: 100%;
  grid-row-gap: 20px;
  margin-top: 30px;
  ${media.mq[1]} {
    display: grid;
    align-items: center;
    grid-column-gap: 20px;
    grid-template-columns: repeat(8, 1fr);
    padding: 30px 35px;
  }
  ${media.mq[2]} {
    margin-bottom: 30px;
  }
  a {
    display: inline-flex;
    justify-content: center;
    text-align: center;
    ${media.mq[1]} {
      grid-column: span 3;
    }
    ${media.mq[2]} {
      grid-column: span 2;
    }
  }
  hr {
    grid-column: 1 / -1;
    width: 100%;
    border: 1.5px solid ${colors.pink50};
  }
`

const ContactSalesCTA = styled.div`
  ${media.mq[1]} {
    grid-column: 1 / 6;
  }
  ${media.mq[2]} {
    grid-column: 1 / 7;
  }
  h3 {
    margin-bottom: 8px;
  }
`

const SalesPoint = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  ${text.paragraph3};
  color: ${colors.darkPurple90};
  ${media.mq[1]} {
    grid-column: span 4;
  }
  ${media.mq[2]} {
    grid-column: span 2;
  }
  p {
    line-height: 1.35;
  }
  img {
    width: 35px;
  }
`

const PeerReviewSection = styled(SectionBlock)`
  padding-top: 0 !important;
`

const PeerReview = styled.div`
  padding: 30px 35px;
  background: ${colors.darkPurple90} !important;
  border-radius: 10px;
  position: relative;
  margin-right: 30px;
  p {
    margin-top: 15px;
  }
  .gatsby-image-wrapper {
    max-width: 50px;
    max-height: 50px;
    position: absolute;
    top: 20px;
    right: 20px;
    object-fit: contain;
    border-radius: 8px;
  }
  ${media.mq[1]} {
    padding-right: 80px;
    margin-right: 0;
  }
  ${media.mq[2]} {
    padding: 45px;
    p {
      width: 88%;
      font-size: 1.55rem;
      line-height: 1.7;
    }
    .gatsby-image-wrapper {
      max-width: 80px;
      max-height: 80px;
      top: 30px;
      right: 30px;
    }
  }
`

const ArrowedButton = styled.a`
    display: flex;
    align-items: center;
    color: ${colors.darkPurple100};
    gap: 10px;
    font-size: 1.1rem;
    font-family: ${fonts.gtHaptikMedium};
    img {
      width: 22px;
      height: 22px;
    }
    &:hover {
      opacity: 70%;
      font-family: ${fonts.gtHaptikMediumRotalic};
    }
  }
`

const PersonaFinal = styled.div`
  position: relative;
  transform: rotate(-6deg) translate(45px, 25px);
  margin: 5px 20px 10px;
  width: 200px;
  text-align: center;
  transition: 0.3s ease-in-out;
  h5 {
    line-height: 1.2;
    position: relative;
    z-index: 1;
  }
  color: ${colors.darkPurple100};
  &:before {
    content: '';
    display: block;
    position: absolute;
    z-index: 0;
    right: -100px;
    bottom: -5px;
    width: 230%;
    height: 170%;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    transform: rotate(5deg);
    background-image: url(${require('./assets/illo-benefit.svg').default});
  }
  ${media.mq[2]} {
    opacity: 0;
  }
`

const PeerReviewWrapper = styled.div`
  padding: 0 20px 45px;
  ${media.mq[1]} {
    padding: 30px 20px 45px;
  }
  &:nth-of-type(even) > a {
    background: ${colors.peach80};
  }
  &:nth-of-type(odd) ${PersonaFinal}:before {
    background-image: url(${require('./assets/illo-benefit-yellow.svg')
      .default});
  }
`

const PersonaBox = styled(Link)`
  background: ${colors.pink20};
  border-radius: 8px;
  padding: 30px 30px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${media.mq[1]} {
    margin-right: 0;

    p {
      min-height: 110px;
    }
  }
  .gatsby-image-wrapper {
    width: 100%;
    transform: translateY(30px);
    border-radius: 8px;
  }
  ${ArrowedButton} {
    align-self: flex-end;
    transform: translateY(20px);
  }
  &:hover {
    ${PersonaFinal} {
      opacity: 1;
    }
    ${ArrowedButton} {
      font-family: ${fonts.gtHaptikMediumRotalic};
      opacity: 0.7;
    }
  }
`

const VideoButton = styled(Button.Primary)`
  background-color: ${colors.darkPurple100};
  justify-content: center;
  align-items: center;
  gap: 12px;
  svg {
    width: 26px;
    transition: 0.2s ease-in-out;
    position: relative;
    top: 0;
    left: 0;
  }
  &:hover {
    svg {
      transform: scale(1.1);
    }
  }
`

const TabArea = styled.div`
  position: relative;
  margin-top: 25px;
  ${media.mq[2]} {
    margin-bottom: 30px;
  }
`

const Tabs = styled.div`
  margin: 20px 0;
  overflow-x: auto;
  display: flex;
  align-items: center;
  column-gap: 12px;
  width: calc(100% + 30px);
  padding: 0 30px 5px 0;
  ${media.mq[1]} {
    margin: 30px auto 20px;
    justify-content: center;
  }
`

const Tab = styled.button`
  display: inline-block;
  ${text.defaultText};
  border: none;
  border-radius: 100px;
  background: ${(p) => (p.active ? colors.darkPurple90 : colors.yellow60)};
  color: ${(p) => (p.active ? colors.yellow100 : colors.darkPurple90)};
  ${(p) => p.active && `font-family: ${fonts.gtHaptikMediumRotalic}`};
  padding: 13px 20px;
  margin: 5px 0;
  cursor: pointer;
  min-width: fit-content;
  &:hover {
    background: ${(p) => (p.active ? colors.darkPurple90 : colors.yellow80)};
    font-family: ${fonts.gtHaptikMediumRotalic};
  }
`

const TabItem = styled.div`
  ${media.mq[1]} {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    align-items: center;
  }
  ${media.mq[2]} {
    column-gap: 30px;
  }
`

const TabItemText = styled.div`
  padding-right: 30px;
  p:nth-of-type(1) {
    ${text.heading3};
    margin: 15px 0 20px;
  }
  a {
    margin: 20px 0 35px;
    &:hover {
      font-family: ${fonts.gtHaptikMediumRotalic};
      color: ${colors.darkPurple70};
    }
  }
`

const TabImage = styled.div`
  text-align: center;
  margin: 30px auto;
  ${media.mq[1]} {
    grid-column: 2 / 3;
    grid-row: 1 / 3;
    margin: auto;
    padding-top: 15px;
  }
  .gatsby-image-wrapper {
    max-width: 90%;
    margin: auto;
    ${media.mq[2]} {
      max-width: 100%;
      margin-right: -3%;
    }
  }
`

const CircleSketch = styled.span`
  position: relative;
  display: inline-block;
  background: url(${require('./assets/icon-circle-sketch.svg').default})
    no-repeat 5% 120% / contain;
`

const ReasonsGrid = styled.div`
  display: grid;
  gap: 20px;
  ${media.mq[1]} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${media.mq[2]} {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: auto 295px;
    grid-auto-flow: dense;
  }
`
const ReasonsGridImage = styled.div`
  padding-top: 20px;
  text-align: center;
  ${media.mq[1]} {
    grid-column: span 2;
  }
  ${media.mq[2]} {
    grid-column: 2 / 3;
  }
`

const ReasonsGridCard = styled.div`
  padding: 25px;
  background: ${colors.peach80};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  h4 + p {
    margin-bottom: 15px;
  }
  ${media.mq[2]} {
    &:nth-of-type(4) {
      grid-column: 2/3;
      margin-top: -140px;
      margin-bottom: 85px;
    }
  }
`

const Separator = styled.div`
  height: 1px;
  border: 1px solid ${colors.peach110};
  margin-top: auto;
  margin-bottom: 15px;
`

const Bubble = styled.div`
  background: ${colors.pink30};
  max-width: 670px;
  margin-top: 20px;
  ${text.heading3};
  colors: ${colors.darkPurple100};
  padding: 20px;
  border-radius: 20px;
  transition: all 0.5s ease-in-out;
  position: relative;
  &:before {
    content: '';
    width: 80px;
    height: 150px;
    position: absolute;
    bottom: -120px;
    left: -10px;
    background: url(${require('./assets/icon-manager.svg').default}) no-repeat
      100% 100% / contain;
    ${media.mq[1]} {
      width: 120px;
      height: 200px;
      bottom: -180px;
    }
  }
  ${media.mq[2]} {
    margin-top: 45px;
    padding: 20px 30px 30px;
    max-width: 950px;
    ${text.heading2};
    margin-left: 60px;
  }
`

const WordBubble = styled.span`
  background: ${colors.pink110};
  color: ${colors.peach10};
  border-radius: 100px;
  padding: 6px 20px 10px;
  display: inline-block;
  transition: width 0.5s ease-in-out;
  overflow: hidden;
  white-space: nowrap;
  vertical-align: middle;
  margin-top: 4px;
  min-width: 100%;
  ${media.mq[1]} {
    min-width: auto;
    text-align: center;
    padding: 4px 20px 12px;
  }
`

const TextCycle = styled.span`
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  &.fade-out {
    opacity: 0;
  }
`

const AfterBubbleText = styled.span`
  display: inline-block;
  transform: translate(6px, 5px);
`

const ResponseBlock = styled.div`
  padding-left: 25%;
  margin: 65px 0 30px;
  ${media.mq[1]} {
    padding-left: 35%;
  }
  ${media.mq[2]} {
    margin-top: 95px;
  }
  + p {
    font-size: 110%;
    margin: 60px auto 30px;
    ${media.mq[2]} {
      margin: 70px auto 45px;
    }
  }
  h2 > span {
    position: relative;
    padding: 0 11%;
    display: inline-block;
    &:before {
      content: '';
      width: 100%;
      height: 100%;
      position: absolute;
      left: -2.2%;
      top: -5%;
      display: block;
      background: url(${require('./assets/icon-sparkle.svg').default}) no-repeat
        100% 100% / contain;
    }
    ${media.mq[1]} {
      padding: 0 8%;
      &:before {
        left: 0;
      }
    }
  }
`

const BigText = styled(text.P1)`
  margin-top: 25px;
  ${media.mq[2]} {
    font-size: 1.55rem;
    line-height: 1.7;
    margin-top: 35px;
  }
  > span {
    display: inline-block;
    position: relative;
    &:before {
      content: '';
      width: 102%;
      left: -1%;
      height: 4px;
      border-radius: 20px;
      background: ${colors.pink100};
      opacity: 0.7;
      bottom: 0;
      position: absolute;
    }
  }
`
const CaseStudyBox = styled.div`
  background: ${colors.pink30};
  border-radius: 8px;
  flex: 1;
  padding: 20px 30px 30px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 30px;
  ${media.mq[1]} {
    gap: 15px;
  }
  h3 {
    line-height: 0.8;
    > span {
      ${text.heading2};
    }
    ${media.mq[2]} {
      padding-right: 5%;
    }
  }
  p {
    margin-bottom: 20px;
  }
  &:nth-of-type(2) {
    background: ${colors.pink20};
  }
  &:nth-of-type(3) {
    background: ${colors.peach50};
  }
`

const CaseStudyImage = styled.div`
  text-align: right;
  margin: auto 0 5px;
  border-bottom: 1px solid ${colors.darkPurple30};
  padding-bottom: 25px;
`

const WhyTrackCTA = styled(ContainerSectionTitle)`
  margin: 45px auto;
`

const SignUpPoints = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 30px;
  row-gap: 15px;
  margin: 20px auto;
`

const SUPoint = styled(text.P2)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LastSignUp = styled(SectionBlock)`
  p {
    margin: 15px auto 0;
    line-height: 1.6;
  }
  background: ${colors.peach10}
    url(${require('./assets/background-shapes.svg').default}) no-repeat center
    bottom / contain;
`

export default IndexPage
